import {
	createContext,
	memo,
	useCallback,
	useContext,
	useMemo,
} from 'react'
import { getUserFromLocalStorage, isCounsellor } from '../../../utils/utils'
import { WELLBEING_SCENARIOS } from '../constants/wellbeingMessages'
import WellbeingModal from '../components/WellbeingModal'
import WellbeingToast from '../components/WellbeingToast'
import { useWellbeingScheduler } from '../hooks/useWellbeingScheduler'
import { markRiskSessionEnded } from '../utils/wellbeingStorage'

const WellbeingContext = createContext(null)

export function WellbeingProvider({ children }) {
	const enabled = isCounsellor()
	const user = getUserFromLocalStorage()
	const userName = user?.profile?.fullName || user?.fullName || 'Counsellor'

	const {
		prompt,
		toast,
		openManual,
		dismiss,
		remindLater,
		complete,
		continuePlatform,
		clearToast,
		breathingAvailable,
		completedToday,
	} = useWellbeingScheduler({ enabled })

	const openBreathingBreak = useCallback(
		(scenario = WELLBEING_SCENARIOS.MANUAL) => {
			openManual(scenario)
		},
		[openManual],
	)

	const value = useMemo(
		() => ({
			openBreathingBreak,
			breathingAvailable,
			completedToday,
			markRiskSessionEnded,
		}),
		[openBreathingBreak, breathingAvailable, completedToday],
	)

	return (
		<WellbeingContext.Provider value={value}>
			{children}
			{enabled ? (
				<>
					<WellbeingModal
						open={Boolean(prompt)}
						scenario={prompt?.scenario || WELLBEING_SCENARIOS.MANUAL}
						userName={userName}
						onDismiss={dismiss}
						onRemindLater={remindLater}
						onContinuePlatform={continuePlatform}
						onComplete={complete}
					/>
					<WellbeingToast
						open={Boolean(toast)}
						message={toast?.message}
						onClose={clearToast}
					/>
				</>
			) : null}
		</WellbeingContext.Provider>
	)
}

export function useWellbeing() {
	const ctx = useContext(WellbeingContext)
	if (!ctx) {
		throw new Error('useWellbeing must be used within WellbeingProvider')
	}
	return ctx
}

export default memo(WellbeingProvider)
